import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';
import _round from 'lodash/round';

import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';
import { Routes } from '../../../routes';
import { useSubmitOrder } from '../../cart/utils/useSubmitOrder';
import { useAlert, AlertConfig } from '../../../modules/alerts';

import { getPaymentMethod } from './useCartPayment';

import {
    ConvergeResponseType,
    CreditCardIncomingMessage,
    PayWithNewCreditCardCallbacks,
    PayWithNewCreditCardOptions,
    useCreditCard,
} from './useCart';

import { useRootSelector } from 'common/features/featuresReducer';
import { CheckoutRequest, emptyCheckoutRequest } from 'common/types/CheckoutRequest';
import {
    PaymentProviderResponse,
    setShippingOptions,
    TokenType,
} from 'common/api/e-comm/orders/service';
import { useHomeDetails } from 'common/features/store/duck/home/utils/useHomeDetails';
import { useShippingMethods } from 'common/hooks/useShippingMethods';
import { isDefined } from 'common/utils';
import {
    get3DSecureToken,
    getCreditCardTransactionToken,
    getZipCodeFromCrmForUser,
    logCreditCardFailure,
} from 'common/api/e-comm/credit-cards/service';
import { getECommFeatureFlagsThunk } from 'common/features/store/duck/feature-flags/duck';
import { ReactComponent as WarningTriangleFilled } from '../../../media/images/warning-triangle-filled.svg';

import { useDispatch } from 'react-redux';

import { closeLoader, openLoader, LoadingOverlay } from 'components/loading-overlay/LoadingOverlay';
import { ThreeDSResponse, ThreeDSTransactionError } from 'common/modules/3dsecure';
import { hasServerError } from 'common/api/utils/hasServerError';
import { getCreditCardTypeFromNumber } from 'common/utils/creditCardValidators';
import { APIResponse, SuccessfulResponse } from 'common/api/models';
import { SuccessfulConvergeResponse } from 'common/api/e-comm/models/Converge';
import { PaymentType } from 'common/api/e-comm/payment-types';
import { SERVICE_UNAVAILABLE_MESSAGE, MAX_SHIP_TO_HOME_CARD_QUANTITY } from 'config';
import { CreditCardType } from 'common/api/e-comm/models/CreditCard';
import { validateCart } from 'common/api/e-comm/cart';
import { FincenContext } from '../components/fincen/context';
import { StatusCode } from 'common/api/config';
import { isOrderExceedsMaxCards } from 'features/cart/utils/cartCheck';
import { getUniversalToken } from 'common/features/store/duck/local-dining/duck';
import { TOKENEX_ID, TOKENEX_URL } from 'config/env';
import { IS_FLIPGIVE } from 'common/env';

interface PartialPayWithNewCreditCardOptions
    extends Omit<PayWithNewCreditCardOptions, 'callbacks'> {
    callbacks: Omit<
        PayWithNewCreditCardCallbacks,
        'onThreeDSecureAuthenticated' | 'onThreeDSecureResult'
    >;
}

export const creditCardErrorConfig: AlertConfig = {
    title: 'Service Temporarily Unavailable',
    message: SERVICE_UNAVAILABLE_MESSAGE,
};

export const creditCardPaymentDeclinedErrorConfig: AlertConfig = {
    title: 'Unable to Process Payment',
    message:
        'Sorry, we were unable to process this payment method. Please try again, or try a different form of payment.',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isSuccessfulConvergePayload = (payload: any): payload is SuccessfulConvergeResponse =>
    isDefined(payload.ssl_card_number);

const getLastFourCCDigits = (message: SuccessfulResponse<CreditCardIncomingMessage>) => {
    if (message.data.type !== ConvergeResponseType.Declined) {
        return;
    }

    return isSuccessfulConvergePayload(message.data.payload)
        ? message.data.payload.ssl_card_number.slice(-4)
        : undefined;
};

export const useCartPlaceOrder = ({
    onThreeDSecureChallengeStart,
    onThreeDSecureChallengeEnd,
}: {
    onThreeDSecureChallengeStart: () => void;
    onThreeDSecureChallengeEnd: () => void;
}) => {
    const { cart } = useRootSelector((s) => s.store.cart);
    const { cartInfo, cartInfoDetails } = useContext(OrderScreenContext);
    const { payment } = cartInfoDetails;
    const homeDetails = useHomeDetails();
    const { loadShippingMethods } = useShippingMethods();
    const alert = useAlert();
    const ref = useRef<LoadingOverlay>(null);
    const fincen = useContext(FincenContext);

    const { payWithNewCreditCard } = useCreditCard();

    const history = useHistory();

    const goToSuccess = () => history.push(Routes.ShopOrderSuccess, cartInfo);

    const showPaymentDeclinedAlert = useCallback(() => {
        alert({
            title: ' ',
            message: (
                <div className="w-full text-grey-1">
                    <div className="flex justify-center p-4">
                        <WarningTriangleFilled />
                    </div>
                    <div className="text-brand-dark text-center text-3xl font-semibold mb-4">
                        {creditCardPaymentDeclinedErrorConfig.title}
                    </div>
                    <div className="text-center text-lg">
                        {creditCardPaymentDeclinedErrorConfig.message}
                    </div>
                </div>
            ),
            largeAlert: true,
            showCloseButton: true,
            buttons: [
                {
                    text: 'Close',
                    secondary: true,
                    className: 'mb-4',
                },
            ],
        });
    }, [alert]);

    const { submitOrder } = useSubmitOrder({
        backToCart: () => history.goBack(),
        clientVersion: '2.0.0',
        openLoader: () => openLoader(ref.current),
        closeLoader: () => closeLoader(ref.current),
        doEarningsMismatchAlert: () => Promise.resolve(true),
        doFinCENAlert: () => {
            // We should show a custom message to the user regarding fincen-limit-exceed
            // TODO (FEC-1098): Check which fincen-limit-exceed message to show here.
            alert(creditCardErrorConfig);
        },
        onCreditCardError: () => {
            showPaymentDeclinedAlert();
        },
        onHold: goToSuccess,
        onSuccess: goToSuccess,
        onUnavailableItems: () => {},
        report: ({ title, message }) => alert({ title, message }),
        shippingMethod: cartInfoDetails.shipping.selectedShipping,
        shouldUpdateDefaultPayment: cartInfo.setPaymentMethodAsDefault,
        cartTotal: cartInfoDetails.total,
    });

    const threeDSecureCallbacks = useMemo(
        () => ({
            onThreeDSecureChallengeStart: async () => {
                await closeLoader(ref.current);
                onThreeDSecureChallengeStart();
            },
            onThreeDSecureChallengeEnd: async () => {
                onThreeDSecureChallengeEnd();
                await openLoader(ref.current);
            },
        }),
        [onThreeDSecureChallengeStart, onThreeDSecureChallengeEnd]
    );

    const dispatch = useDispatch<any>();
    const getPayWithNewCreditCardOptions = useCallback(
        async (
            values: CheckoutRequest,
            total: number
        ): Promise<PartialPayWithNewCreditCardOptions> => {
            if (!cart) {
                return Promise.reject();
            }

            const [tokenResponse, featureFlagsResponse] = await Promise.all([
                getCreditCardTransactionToken(total),
                getECommFeatureFlagsThunk()(dispatch),
            ]);

            if (tokenResponse.error) {
                return Promise.reject(tokenResponse.error);
            }

            let transactionToken3DSecure = '';

            if (featureFlagsResponse.data && featureFlagsResponse.data.enable3DSecure) {
                transactionToken3DSecure = ((await get3DSecureToken()).data || { token: '' }).token;
            }

            return {
                ...tokenResponse.data,
                total,
                transactionToken3DSecure,
                callbacks: threeDSecureCallbacks,
                cartId: cart.id,
                request: values,
            };
        },
        [cart, threeDSecureCallbacks, dispatch]
    );

    /* eslint-disable complexity */
    const placeOrder = async ({
        onCreditCardError,
    }: {
        onCreditCardError?: (cardNumber: string | undefined) => void;
    }) => {
        if (!cart || !cartInfo || !cartInfoDetails || !homeDetails) {
            return;
        }

        // Run Fincen validation one more time
        const result = await validateCart();
        if (result.response?.status === StatusCode.UnprocessableEntity) {
            fincen.processValidateCartResult(result);
            return;
        }

        // Run Cart Validations
        if (cartInfoDetails.shipToHomeIfPossible) {
            if (cart && isOrderExceedsMaxCards(cart)) {
                alert({
                    title: ' ',
                    message: (
                        <div className="w-full text-grey-1">
                            <div className="flex justify-center p-4">
                                <WarningTriangleFilled />
                            </div>
                            <div className="text-brand-dark text-center text-3xl font-semibold mb-4">
                                {MAX_SHIP_TO_HOME_CARD_QUANTITY} Cards Max For Ship to Home Orders
                            </div>
                            <div className="text-center text-lg">
                                Ship to Home orders have a maximum of{' '}
                                {MAX_SHIP_TO_HOME_CARD_QUANTITY} cards. <br />
                                In order to Ship to Home additional cards, you will need to place a
                                separate order.
                            </div>
                        </div>
                    ),
                    largeAlert: true,
                    showCloseButton: true,
                    buttons: [
                        {
                            text: 'Close',
                            secondary: true,
                            className: 'mb-4',
                        },
                    ],
                });
                return;
            }
        }

        const paymentMethod =
            cartInfo.paymentMethodId && getPaymentMethod(cartInfo.paymentMethodId);
        const cartId = cart.id;

        const selectedAddress =
            cartInfoDetails.shipping.selectedAddress ||
            cartInfoDetails.shipping.defaultAddress ||
            undefined;

        if (
            (cartInfoDetails.shipToHomeIfPossible && !selectedAddress) ||
            !payment.payment ||
            !cart ||
            !paymentMethod
        ) {
            return;
        }

        if (paymentMethod) {
            if (!cartInfoDetails.shipToHomeIfPossible) {
                const response = await loadShippingMethods({
                    cartId,
                    customerFirstName: homeDetails.profile.firstName,
                    customerLastName: homeDetails.profile.lastName,
                    customerEmail: homeDetails.profile.email,
                    shipToHomeLineItemIds: undefined,
                    shippingAddress: undefined,
                });

                if (!response.data) {
                    return;
                }

                const shipToCoordinatorSelection = {
                    consignmentId: response.data.shipToCoordinatorConsignment.id,
                    shippingOptionId:
                        response.data.shipToCoordinatorConsignment.shippingOptions[0].id,
                };

                await setShippingOptions({
                    cartId,
                    shipToCoordinatorSelection,
                    shipToHomeSelection: null,
                    paymentMethod,
                });
            } else {
                const response = cartInfoDetails.shipping.shippingMethodsResponse;

                if (!response) {
                    return;
                }

                const shipToHomeSelection = {
                    consignmentId: response.shipToHomeConsignment.id,
                    shippingOptionId: cartInfo.shippingMethodId,
                };

                const shipToCoordinatorSelection = response.shipToCoordinatorConsignment
                    ? {
                          consignmentId: response.shipToCoordinatorConsignment.id,
                          shippingOptionId:
                              response.shipToCoordinatorConsignment.shippingOptions[0].id,
                      }
                    : null;

                await setShippingOptions({
                    cartId,
                    shipToCoordinatorSelection,
                    shipToHomeSelection,
                    paymentMethod,
                });
            }
        }

        let transactionId: string | null = null;
        let paymentProviderResponse: PaymentProviderResponse | null = null;
        let payment3DSecureResponse: ThreeDSResponse | ThreeDSTransactionError | null = null;

        const isNewCreditCard =
            cartInfo.paymentMethodId?.paymentType === PaymentType.CreditCard &&
            (!cartInfo.paymentMethodId.id || cartInfo.paymentMethodId.id === 'new-credit-card');

        const isNewDebitCard =
            cartInfo.paymentMethodId?.paymentType === PaymentType.CreditCard &&
            (!cartInfo.paymentMethodId.id || cartInfo.paymentMethodId.id === 'new-debit-card');

        if (isNewCreditCard || isNewDebitCard) {
            if (!cartInfo.newCreditCard && isNewCreditCard) {
                alert({
                    title: 'No New Credit Card',
                    message: 'There is no info about this new credit card.',
                });
                return;
            }

            if (!cartInfo.newDebitCard && isNewDebitCard) {
                alert({
                    title: 'No New Debit Card',
                    message: 'There is no info about this new debit card.',
                });
                return;
            }

            const total = cartInfoDetails.total;

            if (
                !isDefined(total) ||
                (isNewCreditCard && !isDefined(cartInfo.newCreditCard?.zipCode)) ||
                (isNewDebitCard && !isDefined(cartInfo.newDebitCard?.zipCode))
            ) {
                alert({
                    title: 'Missing Zip Code',
                    message: `The zip code is missing for this ${
                        isNewCreditCard ? 'credit' : 'debit'
                    } card.`,
                });
                return;
            }

            let payWithNewCreditCardOptions;

            try {
                if (isNewCreditCard && cartInfo.newCreditCard) {
                    payWithNewCreditCardOptions = await getPayWithNewCreditCardOptions(
                        {
                            paymentMethod,
                            newCreditCard: cartInfo.newCreditCard,
                            useNewCreditCard: true,
                            savePaymentDetails: !!cartInfo.newCreditCard?.save,
                            shouldUpdateDefaultPayment: cartInfo.setPaymentMethodAsDefault,
                            newDebitCard: emptyCheckoutRequest.newDebitCard,
                            useNewDebitCard: false,
                        },
                        total
                    );
                } else if (isNewDebitCard && cartInfo.newDebitCard) {
                    payWithNewCreditCardOptions = await getPayWithNewCreditCardOptions(
                        {
                            paymentMethod,
                            newCreditCard: cartInfo.newDebitCard,
                            useNewCreditCard: false,
                            savePaymentDetails: !!cartInfo.newDebitCard?.save,
                            shouldUpdateDefaultPayment: cartInfo.setPaymentMethodAsDefault,
                            newDebitCard: cartInfo.newDebitCard,
                            useNewDebitCard: true,
                        },
                        total
                    );
                } else {
                    return;
                }
            } catch (e) {
                if (hasServerError(e as APIResponse<unknown, unknown>)) {
                    alert(creditCardErrorConfig);
                }
                return;
            }

            let transactionToken3DSecure = null;

            const message = (await payWithNewCreditCard({
                ...payWithNewCreditCardOptions,
                callbacks: {
                    ...payWithNewCreditCardOptions.callbacks,
                    onThreeDSecureResult: (
                        threeDSResult: ThreeDSResponse | ThreeDSTransactionError | null
                    ) => {
                        payment3DSecureResponse = threeDSResult;
                    },
                    onThreeDSecureAuthenticated: (token: string) => {
                        transactionToken3DSecure = token;
                    },
                },
            })) as { data: CreditCardIncomingMessage };

            if (isNewCreditCard) {
                paymentProviderResponse = {
                    transactionToken3DSecure,
                    expectedChargeAmount: _round(total, 2),
                    billingAddress:
                        {
                            zipCode: cartInfo.newCreditCard?.zipCode || '',
                        } || null,
                    cardToken: payWithNewCreditCardOptions.token,
                    convergeResponse: JSON.stringify(message.data.payload),
                    tokenType: cartInfo.newCreditCard?.save
                        ? TokenType.NewRecurring
                        : TokenType.NewSingleUse,
                };
            } else {
                paymentProviderResponse = {
                    transactionToken3DSecure,
                    expectedChargeAmount: _round(total, 2),
                    billingAddress: {
                        zipCode: cartInfo.newDebitCard?.zipCode || '',
                    },
                    cardToken: payWithNewCreditCardOptions.token,
                    convergeResponse: JSON.stringify(message.data.payload),
                    tokenType: cartInfo.newDebitCard?.save
                        ? TokenType.NewRecurring
                        : TokenType.NewSingleUse,
                };
            }

            if (message.data.type !== ConvergeResponseType.Approval) {
                if (payment3DSecureResponse) {
                    let cardType = getCreditCardTypeFromNumber(
                        cartInfo.newCreditCard?.cardNumber || ''
                    );
                    if (isNewDebitCard && cartInfo.newDebitCard?.cardNumber) {
                        cardType = getCreditCardTypeFromNumber(cartInfo.newDebitCard.cardNumber);
                    }
                    logCreditCardFailure({
                        cartId: cart.id,
                        paymentProviderResponse,
                        payment3DSecureResponse,
                        cardType: cardType as CreditCardType,
                    });
                }

                // NOTE: Do we want to show a specific error message for 3DS related errors?
                if (
                    onCreditCardError &&
                    (message.data.type === ConvergeResponseType.Declined ||
                        message.data.type === ConvergeResponseType.ThreeDSecureUnauthenticated)
                ) {
                    return onCreditCardError(getLastFourCCDigits(message));
                } else {
                    showPaymentDeclinedAlert();
                }

                return;
            }

            // Elavon Succeeded!
            transactionId = message.data.payload.ssl_txn_id;
        }

        const savePaymentDetails =
            cartInfo.paymentMethodId?.id === 'new-credit-card'
                ? !!cartInfo.newCreditCard?.save
                : !!cartInfo.newDebitCard?.save;

        // [CLO Related Logic For Linking]
        const linkingExistingCard = paymentMethod?.creditCardId === cartInfo.cardToLinkWithClo;
        const cardNumber =
            cartInfo?.newCreditCard?.cardNumber || cartInfo?.newDebitCard?.cardNumber;
        const linkingNewCard = cardNumber && savePaymentDetails && TOKENEX_ID && TOKENEX_URL;

        const shouldProcessLocalDiningLinking = !IS_FLIPGIVE;

        if (shouldProcessLocalDiningLinking && cardNumber && savePaymentDetails) {
            const universalToken = await getUniversalToken(cardNumber, TOKENEX_ID!, TOKENEX_URL!);
            paymentMethod.universalToken = universalToken;
        }

        if (paymentMethod && (linkingExistingCard || linkingNewCard)) {
            const defaultOrgZipCode = homeDetails?.defaultOrganization?.address?.zipCode;
            const zipCode = (await getZipCodeFromCrmForUser()) || defaultOrgZipCode;
            if (zipCode) {
                paymentMethod.enrollCardLinkedOffers =
                    shouldProcessLocalDiningLinking &&
                    Boolean(cartInfo.linkNewCardWithClo || linkingExistingCard);
                paymentMethod.cardLinkedOfferEnrollmentData = {
                    firstName: homeDetails.profile.firstName,
                    lastName: homeDetails.profile.lastName,
                    zipCode,
                };
            }
        }
        // [CLO Related Logic For Linking]

        const request: CheckoutRequest = {
            newCreditCard: cartInfo.newCreditCard || emptyCheckoutRequest.newCreditCard,
            useNewCreditCard: cartInfo.paymentMethodId?.id === 'new-credit-card',
            useNewDebitCard: cartInfo.paymentMethodId?.id === 'new-debit-card',
            newDebitCard: cartInfo.newDebitCard || emptyCheckoutRequest.newDebitCard,
            shouldUpdateDefaultPayment: cartInfo.setPaymentMethodAsDefault,
            savePaymentDetails: savePaymentDetails,
            paymentMethod,
        };

        return submitOrder(
            cart,
            request,
            transactionId,
            paymentProviderResponse,
            payment3DSecureResponse
        );
    };

    return { placeOrder };
};
